import { useState } from 'react';
import { Input } from '../../ui/Input/Input';
import { PrimaryButton } from '../../ui/Button/Buttons';
import { submitRequirementEvidence } from '../../../services/RequirementStepsService';
import { SubmittedEvidenceItem } from '../../../types/RequirementSteps.types';
import {
  evidenceField,
  formatEvidenceDate,
  futureDateMessage,
  futureDated,
  latestEvidence,
  toAttestationDate,
  todayDateInputValue
} from '../../../utils/evidenceHistory';
import { evidenceSubmitError } from '../../../utils/evidenceErrors';
import { invalidUrlMessage, isHttpUrl } from '../../../utils/urlValidation';

const GENERIC_SUBMIT_ERROR = 'Failed to submit. Please try again.';

type RequirementEvidenceUrlProps = {
  actionId: string;
  submittedEvidence?: SubmittedEvidenceItem[] | null;
  onSubmitted: () => void;
};

export function RequirementEvidenceUrl({
  actionId,
  submittedEvidence,
  onSubmitted
}: RequirementEvidenceUrlProps) {
  const [url, setUrl] = useState('');
  const [date, setDate] = useState('');
  const [submitting, setSubmitting] = useState(false);
  // Three slots, not one. The old single `error` was wired to the URL input, so EVERY failure -
  // including a rejected date - red-bordered the URL field and told the user to check a URL that
  // was fine (EAA-1937). `formError` renders outside any Input, so an unmapped failure blames no
  // field at all.
  const [urlError, setUrlError] = useState<string | undefined>();
  const [dateError, setDateError] = useState<string | undefined>();
  const [formError, setFormError] = useState<string | undefined>();

  const latest = latestEvidence(submittedEvidence);
  const latestUrl = evidenceField(latest, 'url');
  // Older rows may hold a schemeless or non-http value; only link what is safe
  // to put in an href, and show the rest as plain text.
  const latestUrlIsLinkable = Boolean(latestUrl && isHttpUrl(latestUrl));

  const handleSubmit = async () => {
    const trimmedUrl = url.trim();
    if (!trimmedUrl || submitting) return;

    // Validate before flipping `submitting`: returning after setting it would
    // strand the button on "Submitting..." with nothing to reset it.
    //
    // Both fields are checked in one pass so two problems surface together rather than one per
    // click, and each message lands on the field that caused it.
    const urlInvalid = !isHttpUrl(trimmedUrl);
    const dateInvalid = Boolean(date) && futureDated(date);
    if (urlInvalid || dateInvalid) {
      setUrlError(urlInvalid ? invalidUrlMessage() : undefined);
      setDateError(dateInvalid ? futureDateMessage() : undefined);
      setFormError(undefined);
      return;
    }

    const attestationDate = date ? toAttestationDate(date) : undefined;

    setUrlError(undefined);
    setDateError(undefined);
    setFormError(undefined);
    setSubmitting(true);
    try {
      await submitRequirementEvidence(
        actionId,
        {
          url: trimmedUrl,
          ...(attestationDate && { attestationDate })
        },
        // Without this the interceptor also toasts, bottom-left and attached to no field, the
        // very problem we are about to show next to the field that caused it.
        { skipErrorToast: true }
      );
      // Clear on success so a later submit cannot silently reuse this value.
      setUrl('');
      setDate('');
      onSubmitted();
    } catch (submitError) {
      console.error('Failed to submit URL evidence:', submitError);
      // Branch on the CODE, never the message (see utils/apiError.ts). The date codes exist so
      // the message can go on the date field rather than the form.
      const { field, message } = evidenceSubmitError(submitError, {
        fallback: GENERIC_SUBMIT_ERROR
      });
      if (field === 'date') setDateError(message);
      else setFormError(message);
    } finally {
      // In `finally`, not the success branch: the component stays mounted across
      // the parent refetch, so a success path that skipped this left the button
      // stuck on "Submitting...".
      setSubmitting(false);
    }
  };

  return (
    <div className="flex flex-col gap-2 mt-2">
      {latestUrl && (
        <div className="flex flex-col gap-0.5 text-xs text-gray-600">
          <span className="font-medium text-gray-700">Current URL</span>
          {latestUrlIsLinkable ? (
            <a
              href={latestUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-purple-800 break-all hover:underline"
            >
              {latestUrl}
            </a>
          ) : (
            <span className="break-all text-gray-700">{latestUrl}</span>
          )}
          {latest?.createdAt && (
            <span>Submitted {formatEvidenceDate(latest.createdAt)}</span>
          )}
        </div>
      )}
      <Input
        id={`evidence-url-${actionId}`}
        label="Evidence URL"
        value={url}
        maxLength={2048}
        onChange={(e) => {
          setUrl(e.target.value);
          if (urlError) setUrlError(undefined);
          if (formError) setFormError(undefined);
        }}
        placeholder="https://..."
        labelClassName="label"
        inputClassName="input"
        error={urlError}
      />
      <Input
        id={`evidence-date-${actionId}`}
        type="date"
        label="Date (optional)"
        value={date}
        maxLength={10}
        // Caps the picker so a future day is not offered at all; the submit-time futureDated
        // check is what catches a keyboard-entered date, which bypasses the cap.
        max={todayDateInputValue()}
        onChange={(e) => {
          setDate(e.target.value);
          if (dateError) setDateError(undefined);
          if (formError) setFormError(undefined);
        }}
        labelClassName="label"
        inputClassName="input"
        error={dateError}
      />
      {formError && (
        <span className="text-sm text-red-600 text-left">{formError}</span>
      )}
      <div className="flex justify-end">
        <PrimaryButton
          size="sm"
          onClick={handleSubmit}
          disabled={!url.trim() || submitting}
        >
          {submitting ? 'Submitting...' : 'Submit'}
        </PrimaryButton>
      </div>
    </div>
  );
}
